/**
 * Chase in one click. "Chase" sends the people you handed a task to a short nudge (their Inbox + a comment on
 * the task) and moves your follow-up two working days out. Click the arrow to edit the message first.
 * For someone outside ONE (waiting on "Supplier X"), it opens a pre-written email instead.
 */
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, ChevronDown, Mail } from "lucide-react";
import { useState } from "react";
import { post } from "../lib/api";

const ago = (iso: string) => {
  const h = Math.round((Date.now() - Date.parse(iso)) / 3_600_000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

export function ChaseButton({ item, compact, iconOnly }: { item: { id: string; title: string; dueAt?: string | null; lastChasedAt?: string | null; chaseCount?: number; waitingOn?: { external?: string; personId?: string } | null; assigneeIds?: string[] }; compact?: boolean; iconOnly?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ to: string[]; followUpOn: string; count: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const last = done ? new Date().toISOString() : item.lastChasedAt;
  const count = done?.count ?? item.chaseCount ?? 0;
  const external = item.waitingOn?.external && !item.waitingOn.personId && !(item.assigneeIds?.length);

  if (external) {
    const body = `Hi,\n\nCould you give me an update on: ${item.title}?${item.dueAt ? `\nIt was due ${new Date(item.dueAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}.` : ""}\n\nThanks,`;
    const href = `mailto:?subject=${encodeURIComponent(`Update: ${item.title}`)}&body=${encodeURIComponent(body)}`;
    // waitingOn.external can be a long note (imported tasks): an icon in lists, a capped label elsewhere — never wider than the row
    if (iconOnly) return <a className="btn btn-sm shrink-0 !px-2" href={href} onClick={(e) => e.stopPropagation()} title={`Email ${item.waitingOn!.external}`} aria-label={`Email ${item.waitingOn!.external}`}><Mail size={13} /></a>;
    return <a className="btn btn-sm min-w-0 max-w-[14rem]" href={href} onClick={(e) => e.stopPropagation()} title={`Email ${item.waitingOn!.external}`}><Mail size={13} className="shrink-0" /> <span className="truncate">Email {item.waitingOn!.external}</span></a>;
  }

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ to: string[]; followUpOn: string; count: number }>(`/api/work/items/${item.id}/chase`, { message: msg.trim() || undefined });
      setDone(r);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["my-day"] });
      void qc.invalidateQueries({ queryKey: ["review-week"] });
      void qc.invalidateQueries({ queryKey: ["item", item.id] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="relative inline-flex shrink-0 flex-col items-end" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <span className="inline-flex">
        <button className={`btn btn-sm rounded-r-none ${iconOnly ? "!px-2" : ""}`} disabled={busy} onClick={() => void send()} title="Chase: send a nudge now" aria-label="Chase"><BellRing size={13} className={done ? "text-emerald-600" : ""} />{!iconOnly && (done ? " Chased" : " Chase")}</button>
        <button className="btn btn-sm rounded-l-none border-l-0 !px-1.5" onClick={() => setOpen((x) => !x)} aria-label="Edit the message first"><ChevronDown size={13} /></button>
      </span>
      {!compact && last && <span className="muted mt-0.5 text-[0.68rem]">chased {ago(last)}{count > 1 ? ` · ${count}×` : ""}</span>}
      {done && <span className="mt-0.5 text-[0.68rem] text-emerald-700">sent to {done.to.join(", ")} · follow-up {new Date(`${done.followUpOn}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric" })}</span>}
      {error && <span className="mt-0.5 max-w-56 text-right text-[0.68rem] text-red-700">{error}</span>}
      {open && (
        <span className="absolute right-0 top-9 z-30 w-72 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 text-left shadow-[var(--shadow-pop)]">
          <textarea className="input min-h-20 text-sm" autoFocus placeholder="Hi …, any update on this?" value={msg} onChange={(e) => setMsg(e.target.value)} />
          <span className="mt-2 flex justify-end gap-2">
            <button className="btn btn-sm" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void send()}>Send</button>
          </span>
        </span>
      )}
    </span>
  );
}
